import { OmissisMarkExtension } from '@framework/form/inputs/rich-text/extensions/OmissisMarkExtension.ts';
import { RichTextOmissisButton } from '@framework/form/inputs/rich-text/menu/buttons/RichTextOmissisButton.tsx';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { type Editor } from '@tiptap/react';
import { useMemo } from 'react';
import { RichTextAdvancedInput } from '../../../../../inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { RichTextAdvancedSchema } from '../../../../../inputs/rich-text-advanced/schema/RichTextAdvancedSchema.ts';
import { useTreeReferencesGlobalContext } from '../../../../../tree/references/TreeReferencesGlobalContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { WidgetType } from '../../../Widget.ts';
import { BaseLayoutColSpanInput } from '../../common/BaseLayoutColSpanInput.tsx';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { CommonGraphicWidgetConfigSchema } from '../common/CommonGraphicWidgetConfigSchema.ts';
import { type IParagraphWidget } from './IParagraphWidget.ts';

export const ParagraphWidgetConfigSchema =
  CommonGraphicWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.Paragraph).required(),
    body: RichTextAdvancedSchema.required()
      .label('Testo')
      .default(() => [
        {
          type: 'text' as const,
          text: null,
        },
      ]),
    label: RichTextAdvancedSchema.label('Etichetta').when(
      'layout.isTableColumn',
      {
        is: true,
        then: schema => schema.required(),
        otherwise: schema => schema.notRequired().strip(),
      },
    ),
  }) satisfies yup.ObjectSchema<IParagraphWidget>;

export interface IParagraphWidgetConfigComponentProps
  extends IWidgetComponentProps<IParagraphWidget> {}

export function ParagraphWidgetConfigComponent(
  props: IParagraphWidgetConfigComponentProps,
) {
  const options = getWidgetProps(props);
  const isEditorLight =
    useTreeReferencesGlobalContext()?.metadata?.configurationMode ===
    'editor-light';
  const extensions = useMemo(() => {
    return isEditorLight ? [OmissisMarkExtension] : undefined;
  }, [isEditorLight]);
  const extendedMenu = useMemo(() => {
    return isEditorLight
      ? (editor: Editor | null) => <RichTextOmissisButton editor={editor} />
      : undefined;
  }, [isEditorLight]);

  return (
    <div>
      <BaseLayoutColSpanInput isTableColumn={options.isTableColumn} />

      {options.isTableColumn && (
        <FormItem name="label" label="Etichetta">
          <RichTextAdvancedInput
            name="label"
            rows={2}
            menus={MenuPresets.Minimal}
          />
        </FormItem>
      )}

      <FormItem name="body" label="Testo">
        <RichTextAdvancedInput
          name="body"
          menus={MenuPresets.Block}
          rows={10}
          extensions={extensions}
          extendedMenu={extendedMenu}
        />
      </FormItem>
    </div>
  );
}
